import React, { useState } from 'react';
import { useCampaign } from '../../contexts/CampaignContext';

// DM-side panel listing every placed trap with controls to edit DC / damage /
// trigger type, manually trigger from outside the map, or delete.
//
// Lives in the DM sidebar; collapses to a single row when no traps exist.

export default function TrapPanel() {
  const { traps, updateTrap, deleteTrap, triggerTrap, tokens } = useCampaign();
  const [expandedId, setExpandedId] = useState(null);

  const list = Object.values(traps || {}).sort(
    (a, b) => (a.createdAt || 0) - (b.createdAt || 0)
  );

  if (list.length === 0) {
    return (
      <div style={styles.panel}>
        <div style={styles.header}>
          <span style={styles.title}>🪤 Traps</span>
        </div>
        <div style={styles.empty}>
          Place a trap with the Hidden ▸ Trap tool, then edit it here.
        </div>
      </div>
    );
  }

  return (
    <div style={styles.panel}>
      <div style={styles.header}>
        <span style={styles.title}>🪤 Traps</span>
        <span style={styles.count}>{list.length}</span>
      </div>
      <div style={styles.list}>
        {list.map((t) => {
          const isOpen = expandedId === t.id;
          return (
            <div key={t.id} style={styles.row}>
              <button
                style={styles.rowHeader}
                onClick={() => setExpandedId(isOpen ? null : t.id)}
              >
                <span style={styles.rowLabel}>
                  {t.triggered ? '🔥 ' : '🪤 '}
                  {t.label || 'Trap'}
                </span>
                <span style={styles.rowMeta}>
                  ({t.x},{t.y}) DC {t.discoveryDC ?? '—'}/{t.reactionDC ?? '—'}
                </span>
                <span style={styles.chevron}>{isOpen ? '▾' : '▸'}</span>
              </button>
              {isOpen && (
                <div style={styles.body}>
                  <Row label="Label">
                    <input
                      style={styles.input}
                      value={t.label || ''}
                      onChange={(e) => updateTrap(t.id, { label: e.target.value })}
                    />
                  </Row>
                  <Row label="Damage">
                    <input
                      style={{ ...styles.input, width: 70 }}
                      value={t.damageDie || ''}
                      placeholder="1d6"
                      onChange={(e) => updateTrap(t.id, { damageDie: e.target.value || null })}
                    />
                    <select
                      style={styles.input}
                      value={t.damageType || 'piercing'}
                      onChange={(e) => updateTrap(t.id, { damageType: e.target.value })}
                    >
                      <option value="piercing">piercing</option>
                      <option value="bludgeoning">bludgeoning</option>
                      <option value="slashing">slashing</option>
                      <option value="fire">fire</option>
                      <option value="cold">cold</option>
                      <option value="poison">poison</option>
                      <option value="acid">acid</option>
                      <option value="lightning">lightning</option>
                      <option value="necrotic">necrotic</option>
                      <option value="radiant">radiant</option>
                      <option value="psychic">psychic</option>
                      <option value="force">force</option>
                    </select>
                  </Row>
                  <Row label="Discovery DC">
                    <input
                      type="number"
                      style={{ ...styles.input, width: 70 }}
                      value={t.discoveryDC ?? ''}
                      placeholder="—"
                      onChange={(e) => {
                        const v = e.target.value === '' ? null : parseInt(e.target.value, 10);
                        updateTrap(t.id, { discoveryDC: Number.isFinite(v) ? v : null });
                      }}
                    />
                  </Row>
                  <Row label="Reaction DC">
                    <input
                      type="number"
                      style={{ ...styles.input, width: 70 }}
                      value={t.reactionDC ?? ''}
                      placeholder="12"
                      onChange={(e) => {
                        const v = e.target.value === '' ? null : parseInt(e.target.value, 10);
                        updateTrap(t.id, { reactionDC: Number.isFinite(v) ? v : null });
                      }}
                    />
                  </Row>
                  <Row label="Trigger">
                    <select
                      style={styles.input}
                      value={t.triggerType || 'enter'}
                      onChange={(e) => updateTrap(t.id, { triggerType: e.target.value })}
                    >
                      <option value="enter">enter cell</option>
                      <option value="adjacent">enter adjacent</option>
                    </select>
                  </Row>
                  <Row label="Secondary">
                    <input
                      style={styles.input}
                      value={t.secondaryEffect || ''}
                      placeholder="(none)"
                      onChange={(e) => updateTrap(t.id, { secondaryEffect: e.target.value || null })}
                    />
                  </Row>
                  <div style={styles.actions}>
                    {!t.triggered && (
                      <button
                        className="btn-ghost btn-sm"
                        style={{ flex: 1 }}
                        onClick={() => {
                          // Pick an adjacent token for the manual trigger, or
                          // fire with a null target if none nearby.
                          const adj = Object.values(tokens || {}).find(
                            (tk) => Math.max(Math.abs(tk.x - t.x), Math.abs(tk.y - t.y)) <= 1
                          );
                          triggerTrap(t.id, adj?.id || null);
                        }}
                      >
                        ⚡ Trigger now
                      </button>
                    )}
                    <button
                      className="btn-danger btn-sm"
                      style={{ flex: 1 }}
                      onClick={() => deleteTrap(t.id)}
                    >
                      🗑 Delete
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div style={styles.fieldRow}>
      <label style={styles.fieldLabel}>{label}</label>
      <div style={styles.fieldInput}>{children}</div>
    </div>
  );
}

const styles = {
  panel:  { background: 'rgba(192,57,43,0.06)', border: '1px solid rgba(192,57,43,0.3)', borderRadius: 6, padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 8 },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  title:  { fontSize: 12, fontFamily: "'Press Start 2P', monospace", color: '#e07070', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700 },
  count:  { fontSize: 11, color: '#9a8b6e', fontVariantNumeric: 'tabular-nums' },
  empty:  { fontSize: 11, color: '#9a8b6e', fontStyle: 'italic', lineHeight: 1.35 },
  list:   { display: 'flex', flexDirection: 'column', gap: 4 },
  row:    { background: '#0e1528', border: '1px solid #2a3552', borderRadius: 4, overflow: 'hidden' },
  rowHeader: { display: 'flex', alignItems: 'center', gap: 6, padding: '6px 10px', width: '100%', background: 'transparent', border: 'none', cursor: 'pointer', color: '#f0e6d0', textAlign: 'left' },
  rowLabel: { fontSize: 12, fontWeight: 700, color: '#fbbf24', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  rowMeta:  { fontSize: 10, color: '#9a8b6e', fontVariantNumeric: 'tabular-nums', flexShrink: 0 },
  chevron:  { fontSize: 10, color: '#9a8b6e' },
  body:     { padding: '8px 10px', borderTop: '1px solid #2a3552', display: 'flex', flexDirection: 'column', gap: 6 },
  fieldRow: { display: 'flex', alignItems: 'center', gap: 8 },
  fieldLabel:{ fontSize: 10, color: '#9a8b6e', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', minWidth: 80 },
  fieldInput:{ display: 'flex', gap: 6, alignItems: 'center', flex: 1 },
  input:    { padding: '4px 6px', background: '#142038', border: '1px solid #4a3d28', borderRadius: 3, color: '#f0e6d0', fontSize: 12, fontFamily: 'system-ui, sans-serif' },
  actions:  { display: 'flex', gap: 6, marginTop: 4 },
};
